import { Dialog } from '@base-ui/react';
import { Button, ButtonVariants } from '@components/button/Button';
import { END_OF_RESTAURANT_DAY } from '@shared/types/shifts';
import { toISODateFormat } from '@shared/utils/dateFormatters';
import { dateToISOTime } from '@shared/utils/time';
import cx from 'classnames';
import { addHours, parseISO, subDays } from 'date-fns';
import { useForm } from 'react-hook-form';
import typography from '~styles/typography.scss';
import type { ShiftUpdateResponse } from './apiHelpers';
import { useCreateShift } from './hooks';
import { ShiftForm, type ShiftFormData } from './ShiftForm';
import styles from './ShiftFormDrawer.scss';

export interface CreateShiftDrawerProps {
  defaultStartDate?: Date;
  defaultEndDate?: Date;
  close: () => void;
  onSuccess?: (response: ShiftUpdateResponse) => void;
  selectTablesEnabled?: boolean;
}

const buildDefaultValuesFromDate = (
  defaultStartDate: Date,
  defaultEndDate?: Date,
): ShiftFormData => {
  const startTime = dateToISOTime(defaultStartDate);

  const startsAfterMidnight = startTime < END_OF_RESTAURANT_DAY;

  const startDate = startsAfterMidnight
    ? subDays(defaultStartDate, 1)
    : defaultStartDate;

  const endTime = defaultEndDate
    ? dateToISOTime(defaultEndDate)
    : dateToISOTime(addHours(startDate, 1));

  const dateAppropriateEndTime = startsAfterMidnight
    ? [endTime, END_OF_RESTAURANT_DAY].sort()[0]
    : endTime;

  return {
    name: '',
    startDate: toISODateFormat(startDate),
    endDate: null,
    startTime: dateToISOTime(startDate),
    endTime: dateAppropriateEndTime,
    repeat: [],
    floorPlanTableIds: [],
    isRecurring: false,
  };
};

export const CreateShiftDrawer = ({
  defaultStartDate,
  defaultEndDate,
  close,
  onSuccess,
  selectTablesEnabled = true,
}: CreateShiftDrawerProps) => {
  const createShiftMutation = useCreateShift({
    onSuccess: (response: ShiftUpdateResponse) => {
      close();
      onSuccess?.(response);
    },
  });

  const form = useForm<ShiftFormData>({
    defaultValues: defaultStartDate
      ? buildDefaultValuesFromDate(defaultStartDate, defaultEndDate)
      : {
          startDate: toISODateFormat(new Date()),
          repeat: [],
          floorPlanTableIds: [],
          isRecurring: false,
        },
  });

  const { handleSubmit } = form;

  const handleOnClickConfirm = handleSubmit((data: ShiftFormData) => {
    const startDateTime = parseISO(data.startDate);
    // If the shift is not recurring, we the end date and repeat values right
    // before making the API request.
    const recurrenceAdjustedMutationArgs = !data.isRecurring
      ? {
          ...data,
          endDate: data.startDate,
          repeat: [startDateTime.getDay().toString()],
        }
      : data;
    createShiftMutation.mutate(recurrenceAdjustedMutationArgs);
  });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={cx(typography.h5, styles.title)}>Create Shift</h1>
        <div className={styles.actions}>
          <Dialog.Close
            render={({ onClick }) => (
              <Button
                label="Cancel"
                onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
                variant={ButtonVariants.Tertiary}
              />
            )}
          />
          <Button
            type="button"
            label="Create"
            onClick={handleOnClickConfirm}
            variant={ButtonVariants.Primary}
          />
        </div>
      </div>
      <div className={styles.content}>
        <ShiftForm form={form} selectTablesEnabled={selectTablesEnabled} />
      </div>
    </div>
  );
};
